/// <amd-module name="Dashboards/DashboardView/src/redux/selectors/TelemetrySelectors" />
import { Dashboard, DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { DashboardViewAwareState } from "../../model/DashboardViewState";
import { Widget } from "../../model/Widget";

import {
    getDashboard,
    getInternalWidgets,
    getInternalWidgetsSeenInitially,
    getModernInternalWidgets,
    getModernInternalWidgetsSeenInitially,
    getWidgets,
    getWidgetsLoaded,
    getWidgetsSeen,
    getWidgetsSeenInitially
} from "./Selectors";

import { WidgetCopyProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";

import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";

/** Describes a breakdown of the composition of a particular dashboard at the time the telemetry was fired */
export interface DashboardCompositionProperties {
    /** The ID of the dashboard */
    dashboardId: string;
    /** identifies the scope of dashboard storage and permissions  */
    dashboardScope: string;
    /** A de-duplicated list of the widget contribution IDs that make up the dashboard */
    widgetContributionIds: string[];
    /** The number of widgets on the dashboard */
    widgetCount: number;
    /** The number of widgets on the dashboard that were developed internally */
    internalWidgetCount: number;
    /** The number of widgets on the dashboard that were developed internally and run on the modern web platform */
    modernInternalWidgetCount: number;
}

export interface IDashboardEditModeProperties {
    /** The ID of the dashboard */
    dashboardId: string;
    /** identifies the scope of dashboard storage and permissions  */
    dashboardScope: string;
}

/** Retrieves dashboard composition telemetry from the state */
export function dashboardCompositionProperties(state: DashboardViewAwareState, pageContext: IVssPageContext): DashboardCompositionProperties {
    const dashboard = getDashboard(state);
    const widgets = getWidgets(state);
    const internalWidgets = getInternalWidgets(state);
    const modernInternalWidgets = getModernInternalWidgets(state, pageContext);

    return {
        dashboardId: dashboard!.id,
        dashboardScope: parseDashboardScope(dashboard!.dashboardScope),
        widgetContributionIds: uniqueSort(widgets.map(w => w.contributionId)),
        widgetCount: widgets.length,
        internalWidgetCount: internalWidgets.length,
        modernInternalWidgetCount: modernInternalWidgets.length,
    };
}

/** Describes runtime telemetry about the composition of a particular dashboard */
export interface DashboardRuntimeCompositionProperties extends DashboardCompositionProperties {
    /** The number of widgets on the dashboard that indicated they've finished loading at TTI.
     *  Independent of load outcome (successful/failed). */
    loadedWidgetCount: number;
    /** The number of widgets on the dashboard that were still loading at TTI */
    loadingWidgetCount: number;
    /** The number of widgets on the dashboard that were visible within the viewport after first render */
    initialViewportWidgetCount: number;
    /** The number of internally developed widgets on the dashboard that were visible
     *  within the viewport after first render */
    initialViewportInternalWidgetCount: number;
    /** The number of internally developed modern web platform widgets on the dashboard that
     *  were visible within the viewport after first render */
    initialViewportModernInternalWidgetCount: number;
}

/** Retrieves dashboard runtime composition telemetry from the state */
export function dashboardRuntimeCompositionProperties(state: DashboardViewAwareState, pageContext: IVssPageContext) {
    const dashboardCompositionTelemetry = dashboardCompositionProperties(state, pageContext);
    const widgetsLoaded = getWidgetsLoaded(state);
    const widgetsSeen = getWidgetsSeen(state);
    const widgetsSeenInitially = getWidgetsSeenInitially(state);
    const internalWidgetsSeenInitially = getInternalWidgetsSeenInitially(state);
    const modernInternalWidgetsSeenInitially = getModernInternalWidgetsSeenInitially(state, pageContext);

    return {
        ...dashboardCompositionTelemetry,
        loadedWidgetCount: widgetsLoaded.length,
        loadingWidgetCount: widgetsSeen.length - widgetsLoaded.length,
        initialViewportWidgetCount: widgetsSeenInitially.length,
        initialViewportInternalWidgetCount: internalWidgetsSeenInitially.length,
        initialViewportModernInternalWidgetCount: modernInternalWidgetsSeenInitially.length
    };
}

/** Describes basic widget properties used by several telemetry events */
export interface BasicWidgetProperties {
    /** The ID of the dashboard the widget is on */
    dashboardId: string;
    /** identifies the scope of dashboard storage and permissions  */
    dashboardScope: string;
    /** The ID of the widget */
    widgetId: string;
    /** The contribution ID of the widget */
    widgetContributionId: string;
}

/** Extends basic widget telemetry properties for use when adding widgets from catalog */
export interface AddFromCatalogWidgetProperties extends BasicWidgetProperties {
    /** True if widget was dragged from catalog */
    dragged?: boolean;
}

/** Retrieves basic widget telemetry from the state and given widget */
function basicWidgetProperties(state: DashboardViewAwareState, widget: Widget): BasicWidgetProperties {
    const dashboard = getDashboard(state);

    return {
        dashboardId: dashboard!.id,
        dashboardScope: parseDashboardScope(dashboard!.dashboardScope),
        widgetId: widget.id!,
        widgetContributionId: widget.contributionId
    };
}

/** Retrieves basic widget telemetry from the state and given widget */
function droppedWidgetProperties(state: DashboardViewAwareState, widget: Widget): AddFromCatalogWidgetProperties {
    const result = basicWidgetProperties(state, widget) as AddFromCatalogWidgetProperties;
    result.dragged = true;
    return result;
}

export const configureWidgetProperties = basicWidgetProperties;
export const deleteWidgetProperties = basicWidgetProperties;
export const addFromCatalogProperties = basicWidgetProperties;
export const dropFromCatalogProperties = droppedWidgetProperties;

/** Describes a basic copy-dashboard operation */
export interface CopyDashboardProperties {
    /** The ID of the source dashboard */
    sourceDashboardId: string;
    /** Identifies the scope of the source dashboard */
    sourceDashboardScope: string;
}

export interface CopyDashboardSuccessProperties extends CopyDashboardProperties {
    /** The ID of the destination dashboard */
    destinationDashboardId: string;
    /** Identifies the scope of the destination dashboard  */
    destinationDashboardScope: string;
    /** Scenario Name for telemetry */
    Scenario?: string
}

export function getCopyDashboardSuccessProperties(state:DashboardViewAwareState, destinationDashboard: {id: string, dashboardScope:DashboardScope}) {
    return {
        destinationDashboardId: destinationDashboard.id,
        destinationDashboardScope: destinationDashboard.dashboardScope ? destinationDashboard.dashboardScope.toString() : ''
    }
}

export function getCopyDashboardProperties(state:DashboardViewAwareState, sourceDashboard: Dashboard) {
    return {
        sourceDashboardId: sourceDashboard && sourceDashboard.id ? sourceDashboard.id : '',
        sourceDashboardScope: sourceDashboard && sourceDashboard.dashboardScope ? sourceDashboard.dashboardScope.toString() : ''
    }
}

/** Describes a copy-to-dashboard operation */
export interface CopyToDashboardProperties {
    /** The contribution ID of the widget being copied */
    widgetContributionId: string;
    /** The ID of the dashboard the widget is on */
    sourceDashboardId: string;
    /** Identifies the scope of the source dashboard storage and permissions */
    sourceDashboardScope: string;
    /** The ID of the dashboard the widget was copied to */
    destinationDashboardId: string;
    /** Identifies the scope of the destination dashboard storage and permissions */
    destinationDashboardScope: string;
    /** The feature originating the copy request */
    source: string;
    /** Whether or not the source and destination are the same */
    isSameDashboard: boolean;
}

/** Retrieves copy-to-dashboard telemetry from the state and given copy action properties */
export function copyToDashboardProperties(state: DashboardViewAwareState, widgetCopyProperties: WidgetCopyProps): CopyToDashboardProperties {
    const sourceDashboard = getDashboard(state);
    const destinationDashboard = widgetCopyProperties.destinationDashboard;

    return {
        widgetContributionId: widgetCopyProperties.sourceWidget.contributionId,
        sourceDashboardId: sourceDashboard!.id,
        sourceDashboardScope: parseDashboardScope(sourceDashboard!.dashboardScope),
        destinationDashboardId: destinationDashboard.id,
        destinationDashboardScope: parseDashboardScope(destinationDashboard.dashboardScope),
        source: "Dashboards", // This varies by consumer feature
        isSameDashboard: sourceDashboard!.id === widgetCopyProperties.destinationDashboard.id
    };
}

function parseDashboardScope(dashboardScope: DashboardScope): string {
    switch (dashboardScope) {
        case DashboardScope.Project:
            return "Project";
        case DashboardScope.Project_Team:
            return "Team";
        default:
            return "";
    }
}
